<template>
  <div class='w-full bg-white flex flex-col relative h-full'>
    <settings-bot-theme-header :is-json-invalid="isJsonInvalid"
                               :variant="saveButtonVariant"
                               :text="saveButtonText"
                               @save-theme="saveTheme"
    />

    <div class="flex h-full">
      <with-loader id="settings-bots-theme-loader"
                   :loading="isLoading"
                   class="h-full w-full"
                   use-as-overlay
      >
        <settings-bot-theme-tabs :themes="themes"
                                 v-model="selectedChatThemeId"
                                 :is-adding-new-theme="isAddingNewTheme"
                                 :theme="selectedTheme"
                                 :image-cache.sync="imageCache"
                                 :is-handling-route-change="isHandlingRouteChange"
                                 @start-delete="isDeleteDialogShown = true"
                                 @start-rename="toggleRenameMode"
                                 @start-adding="addNewTheme"
                                 @rename-theme="renameTheme"
                                 @update:theme="selectedTheme = $event"
                                 @update:json-validity="isJsonInvalid = !$event"
        />
      </with-loader>
      <!-- a separator of header and body of feature (tabs and preview)-->
      <div class="w-full absolute mt-12 h-full border-0 border-t border-soft border-solid z-0"/>
    </div>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="selectedTheme"
                   dialog-type="prompt"
                   delete-title="Delete theme?"
                   :delete-description="deleteDescription"
                   delete-confirm-text="Delete theme"
                   input-placeholder="Enter text"
                   :required-prompt-value="selectedTheme && selectedTheme.name"
                   @cancel="isDeleteDialogShown = false"
                   @delete="deleteTheme"
    />
  </div>
</template>

<script>
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsBotThemeHeader from '@/components/settings/bot-theme/SettingsBotThemeHeader.vue'
import SettingsBotThemeTabs from '@/components/settings/bot-theme/SettingsBotThemeTabs.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import adjust from 'ramda/src/adjust'
import always from 'ramda/src/always'
import any from 'ramda/src/any'
import chatThemeService from '@/services/amio/chat-theme.service'
import assoc from 'ramda/src/assoc'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import clone from 'ramda/src/clone'
import equals from 'ramda/src/equals'
import find from 'ramda/src/find'
import findIndex from 'ramda/src/findIndex'
import forEach from 'ramda/src/forEach'
import head from 'ramda/src/head'
import ifElse from 'ramda/src/ifElse'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import map from 'ramda/src/map'
import not from 'ramda/src/not'
import pipe from 'ramda/src/pipe'
import prop from 'ramda/src/prop'
import propEq from 'ramda/src/propEq'
import propOr from 'ramda/src/propOr'
import reject from 'ramda/src/reject'
import toaster from '@/utils/toaster'
import when from 'ramda/src/when'
import { APPEARANCE_STRINGS, DEFAULT_APPEARANCE_JSON, DEFAULT_THEME } from '@/constants/chat-theme.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { ITEM_IDS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import errorUtils from '@/utils/error.utils'
import fileService from '@/services/amio/file.service'
import previewModule from '@/store/modules/preview.module'
import dissoc from 'ramda/src/dissoc'

const SENT_MESSAGE_KEY_ORDER = [ 'wrapperBg', 'bg', 'text', 'button', 'carouselButton', 'qr', 'file' ]
const RECEIVED_MESSAGE_KEY_ORDER = [ 'bg', 'marginRight', 'text', 'file' ]

export default {
  name: 'SettingsBotTheme',
  components: {
    DeleteDialog,
    SettingsBotThemeHeader,
    SettingsBotThemeTabs,
    SettingsItemHeader,
    WithLoader
  },

  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        if (!params.chatThemeId) this.selectedChatThemeId = ITEM_IDS.NEW
      }
    },

    selectedChatThemeId(newId, oldId) {
      window.clearTimeout(this.saveButtonStateTimeout)
      if (oldId !== ITEM_IDS.NEW) this.setButtonProps()
    }
  },

  data() {
    return {
      isLoading: false,
      isDeleteDialogShown: false,
      isEmpty,
      themes: [],
      APPEARANCE_STRINGS,
      isJsonInvalid: false,
      originalThemesMap: {},
      BUSY_BUTTONS,
      imageCache: {
        headerLogo: null,
        buttonIcon: null
      },
      saveButtonStateTimeout: null,
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: SAVE_BUTTON_LABELS.DEFAULT,
      isHandlingRouteChange: false
    }
  },

  computed: {
    error: previewModule.error,

    selectedChatThemeId: {
      get() {
        return this.$route.params.chatThemeId
      },
      set(newChatThemeId) {
        if (this.selectedChatThemeId === newChatThemeId) return
        this.$router.replace({ params: { chatThemeId: newChatThemeId } })
      }
    },

    deleteDescription() {
      return `
        This will delete all data for "${this.selectedTheme.name}."
        It cannot be undone.<br/><br/>Please type <strong>${this.selectedTheme.name}</strong> to enable delete.
      `
    },

    selectedTheme: {
      get() {
        return find(propEq('id', this.selectedChatThemeId))(this.themes) || {}
      },
      set({ name, themeConfig, langConfig }) {
        if (themeConfig) {
          this.updateThemeProp('themeConfig', themeConfig)
        }
        if (langConfig) {
          this.updateThemeProp('langConfig', langConfig)
        }
        if (!isNil(name)) {
          this.updateThemeProp('name', name)
          this.updateThemeProp('isEdited', false)
        }
      }
    },

    isAddingNewTheme() {
      return any(propEq('id', ITEM_IDS.NEW))(this.themes)
    },

    isThemeExisting() {
      if (isNil(this.$route.params.chatThemeId)) {
        return false
      }
      if (this.selectedChatThemeId === ITEM_IDS.NEW) {
        return false
      }

      return !isEmpty(this.selectedTheme)
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.reviewUnsavedChanges(to, from, next)
  },

  beforeRouteLeave(to, from, next) {
    this.reviewUnsavedChanges(to, from, next)
  },

  methods: {
    async reviewUnsavedChanges(to, from, next) {
      if (this.hasUnsavedChanges(this.selectedChatThemeId)) {
        this.isHandlingRouteChange = true
        const discard = confirm(APPEARANCE_STRINGS.unsavedChangesConfirmation)

        if (discard) {
          this.resetThemeChanges(from.params.chatThemeId)
          next()
          // Keep the flag true until after the route change is complete
          await this.$nextTick()
          setTimeout(() => {
            this.isHandlingRouteChange = false
          }, 100)
        } else {
          this.isHandlingRouteChange = false
          if (to.params.chatThemeId === ITEM_IDS.NEW) {
            this.cancelAddNewTheme()
          }
          next(false)
        }
      } else {
        next()
      }
    },
    updateThemeProp(prop, newValue) {
      this.themes = map(
        when(
          propEq('id', this.selectedChatThemeId),
          assoc(prop, newValue)
        ),
        this.themes
      )
    },

    hasUnsavedChanges(chatThemeId) {
      const originalTheme = this.originalThemesMap[chatThemeId]
      if (!originalTheme) return false

      const cleanedOriginalTheme = dissoc('isEdited', originalTheme)
      const cleanedSelectedTheme = dissoc('isEdited', this.selectedTheme)
      return (
        !equals(cleanedSelectedTheme, cleanedOriginalTheme) ||
        this.imageCache.headerLogo ||
        this.imageCache.buttonIcon
      )
    },

    resetThemeChanges(chatThemeId) {
      const originalTheme = this.originalThemesMap[chatThemeId]
      if (!originalTheme) return

      const index = findIndex(propEq('id', chatThemeId))(this.themes)
      if (index !== -1) {
        this.$set(this.themes, index, clone(originalTheme))
      }
    },

    async uploadHeaderLogo() {
      if (!this.imageCache.headerLogo) return
      const response = await fileService.create(this.imageCache.headerLogo)
      this.selectedTheme.themeConfig.header.logoUrl = response.url
      this.imageCache.headerLogo = null
    },

    async uploadButtonIcon() {
      if (!this.imageCache.buttonIcon) return
      const response = await fileService.create(this.imageCache.buttonIcon)
      this.selectedTheme.themeConfig.launcher.imageUrl = response.url
      this.imageCache.buttonIcon = null
    },

    async saveNewTheme() {
      await this.uploadHeaderLogo()
      await this.uploadButtonIcon()
      const newTheme = await chatThemeService.create(this.selectedTheme)
      const chatThemeId = newTheme.id
      const index = findIndex(propEq('id', ITEM_IDS.NEW))(this.themes)
      const updateFn = pipe(
        assoc('id', chatThemeId),
        assoc('isEdited', false)
      )
      this.themes = adjust(index, updateFn, this.themes)
      this.$delete(this.originalThemesMap, ITEM_IDS.NEW)
      const savedTheme = find(propEq('id', chatThemeId))(this.themes)
      if (savedTheme) {
        this.$set(this.originalThemesMap, chatThemeId, clone(savedTheme))
      }

      await this.$router.replace({ params: { chatThemeId } })
    },

    async saveExistingTheme() {
      await this.uploadHeaderLogo()
      await this.uploadButtonIcon()
      await chatThemeService.update(this.selectedTheme)
      const index = findIndex(propEq('id', this.selectedChatThemeId))(this.themes)
      const updateFn = assoc('isEdited', false)
      this.themes = adjust(index, updateFn, this.themes)

      const savedTheme = find(propEq('id', this.selectedChatThemeId))(this.themes)
      if (savedTheme) {
        this.$set(this.originalThemesMap, this.selectedChatThemeId, clone(savedTheme))
      }
    },

    async saveTheme() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.APPEARANCE_SAVE, true)
      this.isLoading = true
      this.error = null

      try {
        if (this.isAddingNewTheme) {
          await this.saveNewTheme()
        } else {
          await this.saveExistingTheme()
        }
        toaster.success(APPEARANCE_STRINGS.saveSuccess)
      } catch (err) {
        toaster.error(err)
        this.error = err
      } finally {
        this.startButtonStateTimeout()
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.APPEARANCE_SAVE, false)
        this.isLoading = false
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.saveButtonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
    },

    addNewTheme(startAdding) {
      if (startAdding) {
        this.selectedChatThemeId = ITEM_IDS.NEW
        const newTheme = { ...DEFAULT_THEME, id: ITEM_IDS.NEW, name: '', isEdited: true }
        this.imageCache = { headerLogo: null, buttonIcon: null }
        this.themes.unshift(newTheme)
        this.$set(this.originalThemesMap, ITEM_IDS.NEW, JSON.parse(JSON.stringify(newTheme)))
      } else {
        this.cancelAddNewTheme()
        this.selectedChatThemeId = this.themes[0].id
      }
    },

    cancelAddNewTheme() {
      this.themes = reject(propEq('id', ITEM_IDS.NEW))(this.themes)
      this.$delete(this.originalThemesMap, ITEM_IDS.NEW)
    },

    toggleRenameMode(chatThemeId) {
      const index = findIndex(propEq('id', chatThemeId))(this.themes)
      if (index === -1) return

      const updateTheme = (theme) => {
        const currentIsEdited = propOr(false, 'isEdited', theme)
        return assoc('isEdited', not(currentIsEdited), theme)
      }

      this.themes = adjust(index, updateTheme, this.themes)
    },

    async renameTheme(renamedTheme) {
      if (renamedTheme.id === ITEM_IDS.NEW) {
        this.selectedTheme = {
          ...this.selectedTheme,
          name: renamedTheme.name,
          isEdited: false
        }
        return
      }

      try {
        const updateData = { ...this.selectedTheme, name: renamedTheme.name }
        await chatThemeService.update(updateData)
        this.selectedTheme = {
          ...updateData,
          isEdited: false
        }
      } catch (err) {
        toaster.error(err)
      }
    },

    async deleteTheme() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      this.isLoading = true
      try {
        const idToDelete = this.selectedChatThemeId
        this.$delete(this.originalThemesMap, idToDelete)
        await chatThemeService.delete(this.selectedChatThemeId)
        toaster.success(`Theme ${this.selectedTheme.name} deleted successfully.`)
        this.themes = reject(propEq('id', this.selectedChatThemeId))(this.themes)
        if (isEmpty(this.themes)) {
          this.addNewTheme()
        } else {
          this.selectedChatThemeId = prop('id', head(this.themes))
        }
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.isDeleteDialogShown = false
        this.isLoading = false
      }
    },

    async initTheme() {
      this.isLoading = true
      try {
        this.themes = await chatThemeService.list()

        this.themes = map(theme => {
          if (isEmpty(theme.themeConfig)) {
            return assoc('themeConfig', DEFAULT_APPEARANCE_JSON, theme)
          }
          return { ...theme, themeConfig: this.reorderThemeMessages(theme.themeConfig) }
        }, this.themes)

        if (isEmpty(this.themes)) {
          this.themes.unshift({ ...DEFAULT_THEME, id: ITEM_IDS.NEW })
          this.selectedTheme = this.themes[0]
        }
        forEach(a => {
          this.$set(this.originalThemesMap, a.id, clone(a))
        }, this.themes)

        if (!this.isThemeExisting) {
          this.selectedChatThemeId = ifElse(
            isEmpty,
            always('new'),
            pipe(head, prop('id'))
          )(this.themes)
        }
      } catch (err) {
        toaster.error(err.message, err)
      } finally {
        this.isLoading = false
      }
    },

    reorderMessageKeys(messageObj, keyOrder) {
      if (!messageObj) return messageObj
      const ordered = {}
      keyOrder.forEach(key => {
        if (messageObj.hasOwnProperty(key)) {
          ordered[key] = messageObj[key]
        }
      })
      // Add any extra keys at the end
      Object.keys(messageObj).forEach(key => {
        if (!ordered.hasOwnProperty(key)) {
          ordered[key] = messageObj[key]
        }
      })
      return ordered
    },

    reorderThemeMessages(themeConfig) {
      return {
        ...themeConfig,
        sentMessage: this.reorderMessageKeys(themeConfig.sentMessage, SENT_MESSAGE_KEY_ORDER),
        receivedMessage: this.reorderMessageKeys(themeConfig.receivedMessage, RECEIVED_MESSAGE_KEY_ORDER)
      }
    },

    hideIntercom() {
      const intercom = document.querySelector('.intercom-lightweight-app')
      if (intercom) {
        intercom.style.display = 'none'
      }
    },

    showIntercom() {
      const intercom = document.querySelector('.intercom-lightweight-app')
      if (intercom) {
        intercom.style.display = 'block'
      }
    }
  },

  async mounted() {
    this.hideIntercom()
    await this.initTheme()

    // Set up MutationObserver to watch for Intercom widget
    this._intercomObserver = new MutationObserver(() => {
      this.hideIntercom()
    })
    this._intercomObserver.observe(document.body, { childList: true, subtree: true })
  },

  beforeDestroy() {
    if (this._intercomObserver) {
      this._intercomObserver.disconnect()
      this.showIntercom()
    }
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  @apply opacity-0;

  transform: translateX(10px);
}
</style>
